import React, { useState, useEffect, useCallback } from 'react';
import { User, UserStatus, Tag } from './types/user';
import { UserRole } from '../settings/UserRoles';
import { useAdmin } from '../../providers/AdminProvider';
import UserInviteForm from './subcomponents/UserInviteForm';
import UserFilters from './subcomponents/UserFilters';
import UserTable from './subcomponents/UserTable';
import BulkActions from './subcomponents/BulkActions';
import Reminder from '../ReminderToast';
import { downloadCSV, generateCSV, getAlertBackgroundColor, getAlertIcon, getAlertIconColor } from './utils/adminUtils';
import { useVetRec } from '../../providers/VetRecProvider';
import { Invitation } from '../../serverActions/admin';
import ConfirmationModal from './subcomponents/ConfirmationModal';
import UnsavedChangesBanner from '../templates/UnsavedChangesBanner';

const UserManagement: React.FC = () => {
  const { users, teams, tags, updateUserRole, bulkUpdateUsersTag, bulkUpdateUsersTeams, hideAlertBanner, alertBanner, alertMessage, alertTitle, alertType, resendInvite, revokeInvite, removeUser, pendingUserChanges, clearUserChanges, fetchData } = useAdmin()
  const { superOrgMetadata } = useVetRec()
  const [filteredUsers, setFilteredUsers] = useState<User[]>(users);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [filterName, setFilterName] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [selectAll, setSelectAll] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState<string>();
  const [confirmMessage, setConfirmMessage] = useState<string>();
  const [confirmAction, setConfirmAction] = useState<(() => Promise<void>)>();

  // Filter users when filter criteria change
  useEffect(() => {
    let result = users;

    if (filterName) {
      result = result.filter(user =>
        user.name && user.name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    if (filterEmail) {
      result = result.filter(user =>
        user.email.toLowerCase().includes(filterEmail.toLowerCase())
      );
    }

    if (filterTeam) {
      result = result.filter(user =>
        user.teams.includes(filterTeam)
      );
    }

    if (filterTag) {
      if (filterTag === 'No Tag') {
        result = result.filter(user =>
          user.tags.length === 0
        );
      }
      else {
        result = result.filter(user =>
          user.tags.includes(filterTag)
        );
      }
    }

    if (filterRole) {
      result = result.filter(user =>
        user.role === filterRole as UserRole
      );
    }

    if (filterStatus) {
      result = result.filter(user =>
        user.status === filterStatus as UserStatus
      );
    }

    setFilteredUsers(result);
  }, [users, filterName, filterEmail, filterTeam, filterTag, filterRole, filterStatus]);

  // Handle "select all" functionality
  useEffect(() => {
    if (selectAll) {
      setSelectedUsers(filteredUsers
        .map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  }, [selectAll, filteredUsers]);

  const handleSelectUser = useCallback((userId: string) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  }, [selectedUsers]);

  const handleToggleSelectAll = () => {
    setSelectAll(!selectAll);
  };

  const clearFilters = () => {
    setFilterName('');
    setFilterEmail('');
    setFilterTeam('');
    setFilterTag('');
    setFilterRole('');
    setFilterStatus('');
  };

  const handleInviteSuccess = () => {
    setShowInviteForm(false);
  };

  const handleCancelInvite = () => {
    setShowInviteForm(false);
  };

  // New function to directly update a user's teams
  const handleUpdateUserTeams = async (usersToUpdate: User[] | User, teamsToAdd: string[], teamsToRemove: string[]) => {
    const usersArray = Array.isArray(usersToUpdate) ? usersToUpdate : [usersToUpdate];
    await bulkUpdateUsersTeams(usersArray, teamsToAdd, teamsToRemove);
  };

  // New function to directly update a user's tags
  const handleUpdateUserTags = async (usersToUpdate: User[] | User, tagToAdd: Tag) => {

    const userToUpdateArray = Array.isArray(usersToUpdate) ? usersToUpdate : [usersToUpdate];

    // TODO check if tag change will leave any teams without any users or without admins

    await bulkUpdateUsersTag(userToUpdateArray, tagToAdd);
  };

  // Add function to handle role updates
  const handleUpdateUsersRole = async (users: User[], newRole: UserRole) => {
    await updateUserRole(users, newRole);
  };

  const handleClearSelection = () => {
    setSelectedUsers([]);
    setSelectAll(false);
  }

  const handleResendInvite = async (invitations: Invitation[]) => {
    await resendInvite(invitations);
  }

  const handleRevokeInvite = async (invitations: Invitation[]) => {
    await revokeInvite(invitations);
  }

  const handleRemoveUser = async (userId: string[]) => {
    setConfirmTitle("Remove User(s)");
    setConfirmMessage("Are you sure you want to remove the selected user(s)? This action cannot be undone.");
    setConfirmAction(() => async () => {
      await removeUser(userId);
      setConfirmTitle(undefined);
      setConfirmMessage(undefined);
      setConfirmAction(undefined);
    });
  }

  const handleSaveAllChanges = async () => {
    const promises = Object.entries(pendingUserChanges).map(async ([userId, changes]) => {
      const user = users.find(u => u.id === userId);
      if (!user) return
      const oldRole = user.role;
      user.role = changes.role || oldRole; // Update role in object so that team changes are correct

      // Start by changing tags
      if (changes.tags) {
        const tagToAdd = tags.find(tag => tag.id === (changes.tags ?? [])[0]);
        if (tagToAdd) await handleUpdateUserTags(user, tagToAdd);
      } // If we are doing a change in tags, then don't do a change in teams as they will be overwritten by the tag
      else if (changes.teams) {
        const teamsToAdd = changes.teams.filter(team => !user.teams.includes(team));
        const teamsToRemove = user.teams.filter(team => !(changes.teams ?? []).includes(team));
        await handleUpdateUserTeams(user, teamsToAdd, teamsToRemove);
        user.teams = [...user.teams.filter(team => !teamsToRemove.includes(team)), ...teamsToAdd]; // update teams so that roles later on are correct
      }

      // Change role
      if (changes.role && changes.role !== oldRole) {
        await handleUpdateUsersRole([user], changes.role);
      }
    });

    await Promise.all(promises);
    await fetchData(['users', 'teams', 'tags']);
    clearUserChanges();
  };

  return (
    <div className="max-w-7xl mx-auto py-8">
      <UnsavedChangesBanner show={Object.keys(pendingUserChanges).length > 0} onSave={() => {handleSaveAllChanges().catch(() => {})}} />
      {confirmAction !== undefined && <ConfirmationModal showModal={confirmTitle !== undefined} confirmTitle={confirmTitle} confirmMessage={confirmMessage} confirmAction={confirmAction} rejectAction={() => { setConfirmTitle(undefined); setConfirmMessage(undefined); setConfirmAction(undefined) }} color='red'/>}
      <Reminder show={alertBanner} hide={hideAlertBanner} text={alertMessage} title={alertTitle} iconColor={getAlertIconColor(alertType)} icon={getAlertIcon(alertType)} lineColor={getAlertBackgroundColor(alertType)} />
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
          <p className="mt-2 text-sm text-gray-700">
            A list of all users in your organization including their name, email, team and tag assignments.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => setShowInviteForm(!showInviteForm)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-xs sm:text-sm font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full sm:w-auto"
          >
            {showInviteForm ? 'Cancel Invite' : 'Invite User'}
          </button>
        </div>
      </div>

      {/* User Invite Form */}
      {showInviteForm && (
        <div className="mt-6">
          <UserInviteForm
            onInviteSuccess={handleInviteSuccess}
            onCancel={handleCancelInvite}
          />
        </div>
      )}

      {/* Filters */}
      {!showInviteForm && (
        <UserFilters
          filterName={filterName}
          filterEmail={filterEmail}
          filterTeam={filterTeam}
          filterTag={filterTag}
          filterRole={filterRole}
          filterStatus={filterStatus}
          teams={teams}
          tags={tags}
          onFilterNameChange={setFilterName}
          onFilterEmailChange={setFilterEmail}
          onFilterTeamChange={setFilterTeam}
          onFilterTagChange={setFilterTag}
          onFilterRoleChange={setFilterRole}
          onFilterStatusChange={setFilterStatus}
          onClearFilters={clearFilters}
        />
      )}

      {/* Bulk actions */}
      {!showInviteForm && selectedUsers.length > 0 && (
        <BulkActions
          selectedUsers={users.filter(user => selectedUsers.includes(user.id))}
          selectedUsersCount={selectedUsers.length}
          teams={teams}
          tags={tags}
          clearSelection={() => handleClearSelection()}
          onResendInvite={handleResendInvite}
          onRevokeInvite={handleRevokeInvite}
        />
      )}

      {/* Users table */}
      {!showInviteForm && (
        <UserTable
          users={filteredUsers}
          selectedUsers={selectedUsers}
          selectAll={selectAll}
          teams={teams}
          tags={tags}
          onSelectAll={handleToggleSelectAll}
          onSelectUser={handleSelectUser}
          onResendInvite={handleResendInvite}
          onRevokeInvite={handleRevokeInvite}
          onRemoveUser={handleRemoveUser}
          saveAllChanges={handleSaveAllChanges}
        />
      )}
      {!showInviteForm && <div className='flex justify-end gap-x-2'>
        <div className='text-xs sm:text-sm text-white mt-2 px-1 sm:px-2 bg-blue-600 shadow-sm border border-gray-300 rounded-md py-1 cursor-pointer hover:bg-blue-500' onClick={() => {
          const csv = generateCSV(filteredUsers, tags, teams);
          downloadCSV(csv, `${String(superOrgMetadata?.name || "users")}.csv`);
        }}>
          <span className='font-semibold'>Export Users to CSV</span>
        </div>
        <div className='text-xs sm:text-sm text-gray-700 mt-2 px-1 sm:px-2 bg-white shadow-sm border border-gray-300 rounded-md py-1'>
          <span className='font-semibold'>User Count:</span> {filteredUsers.length}
        </div>
      </div>}
    </div>
  );
};

export default UserManagement;
